import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { can } from '../core/access';
import { formatClock } from '../core/format';
import { reconcileGrid } from '../devices/device-grid';
import { deviceNameHtml } from '../devices/device-card';
import { showInfoModal } from '../devices/device-modals';
import { showMercadoDeviceModal } from './mercado-device-modal';
import { showMercadoLogModal } from './mercado-log-modal';
import { openClickerOperationsWindow } from '../clicker/clicker-operations-modal';
import { openClickerHistoryWindow } from '../clicker/clicker-history-modal';
import { clickerStartSvg, clickerStopSvg, clickerSettingsSvg, clickerLogSvg, clickerRegistrySvg, clickerHistorySvg, infoSvg, walletGlyphSvg, } from '../stream/stream-controls-shared';
const POLL_MS = 10000;
let poll = null;
function stateLabel(d) {
    if (d.state === 'running')
        return t('farmdev.mercado_state_running');
    if (d.state === 'error')
        return t('farmdev.mercado_state_error');
    // Wanted but not running yet: the reconcile loop will (re)start it.
    return d.polling ? t('farmdev.mercado_state_pending') : t('farmdev.mercado_state_stopped');
}
function showMercadoInfoModal(d) {
    const rows = [
        { label: t('farmdev.device_id'), value: d.udid },
        { label: t('farmdev.mercado_account'), value: d.nickname ? `${d.nickname} (${d.mpUserId})` : String(d.mpUserId) },
        { label: 'CVU', value: d.cvu },
        { label: t('farmdev.mercado_token'), value: d.tokenHint },
        { label: t('farmdev.mercado_state'), value: stateLabel(d) },
        { label: t('farmdev.lazy_poll'), value: d.lazyPoll ? t('common.yes') : t('common.no') },
        { label: t('farmdev.lazy_expected'), value: d.expectedDeposits === null ? null : String(d.expectedDeposits) },
        { label: t('farmdev.mercado_last_poll'), value: d.lastPollAt ? new Date(d.lastPollAt).toLocaleString() : null },
        { label: t('farmdev.mercado_last_deposit'), value: d.lastDepositAt ? new Date(d.lastDepositAt).toLocaleString() : null },
        { label: t('farmdev.mercado_deposits_sent'), value: String(d.depositsSent) },
        { label: t('farmdev.adspower_last_error'), value: d.lastError || null },
    ]
        .filter((r) => !!r.value);
    showInfoModal(d.label, rows);
}
/** The stage placeholder: poller state as the headline, CVU / last poll / deposits under it. */
function placeholderHtml(d) {
    const lastPoll = d.lastPollAt ? formatClock(d.lastPollAt) : '—';
    return `
        <div class="device-player-placeholder">
            <span>${walletGlyphSvg}</span>
            <strong>${stateLabel(d)}</strong>
            <span class="aps-meta">CVU: ${esc(d.cvu)}</span>
            <span class="aps-meta">${t('farmdev.mercado_last_poll')}: ${esc(lastPoll)}</span>
            ${d.state === 'running' && d.lazyPoll ? `<span class="aps-meta">${d.expectedDeposits ? `${t('farmdev.lazy_expected')}: ${d.expectedDeposits}` : t('farmdev.lazy_waiting')}</span>` : ''}
            <span class="aps-meta">${t('farmdev.mercado_deposits_sent')}: ${d.depositsSent}</span>
            ${d.lastError ? `<span class="aps-meta aps-meta--error" title="${esc(d.lastError)}">${esc(d.lastError)}</span>` : ''}
        </div>`;
}
function makeMercadoDeviceCard(d, farmId) {
    const card = document.createElement('div');
    const live = d.state === 'running';
    card.className = `device-card device-card--mercado ${live ? 'online' : 'offline'}`;
    card.dataset['udid'] = d.udid;
    const editBtn = can('farm.settings')
        ? `<button type="button" class="stream-control-btn mpd-edit" title="${t('common.edit')}" aria-label="${t('common.edit')}">${clickerSettingsSvg}</button>`
        : '';
    const logBtn = can('device.clickerLog')
        ? `<button type="button" class="stream-control-btn mpd-log" title="${t('farmdev.clicker_logs')}" aria-label="${t('farmdev.clicker_logs')}">${clickerLogSvg}</button>`
        : '';
    const historyBtn = can('device.clickerHistory')
        ? `<button type="button" class="stream-control-btn mpd-history" title="${t('farmdev.clicker_history')}" aria-label="${t('farmdev.clicker_history')}">${clickerHistorySvg}</button>`
        : '';
    const runTitle = d.polling ? t('farmdev.mercado_stop') : t('farmdev.mercado_start');
    card.innerHTML = `
        <div class="device-card__header">
            <span class="status-dot ${live ? 'status-dot--green' : 'status-dot--red'}"></span>
            <span class="device-name-wrap">${deviceNameHtml(d.label, d.udid)}</span>
        </div>
        <div class="device-card__player">
            <div class="device-stream-stage">
                <div class="device-stream-canvas-wrap">${placeholderHtml(d)}</div>
                <div class="device-stream-controls" aria-label="${t('farmdev.device_controls')}">
                    <button type="button" class="stream-control-btn mpd-info" title="${t('farmdev.device_info')}" aria-label="${t('farmdev.device_info')}">${infoSvg}</button>
                    <span class="stream-controls__divider"></span>
                    <button type="button" class="stream-control-btn mpd-run duo-clicker ${d.polling ? 'is-running' : ''}" title="${runTitle}" aria-label="${runTitle}">${d.polling ? clickerStopSvg : clickerStartSvg}</button>
                    ${editBtn}
                    ${logBtn}
                    <button type="button" class="stream-control-btn mpd-ops" title="${t('farmdev.operations_registry')}" aria-label="${t('farmdev.operations_registry')}">${clickerRegistrySvg}</button>
                    ${historyBtn}
                </div>
            </div>
        </div>
    `;
    card.querySelector('.mpd-info')?.addEventListener('click', () => showMercadoInfoModal(d));
    card.querySelector('.mpd-edit')?.addEventListener('click', () => {
        showMercadoDeviceModal(farmId, d, () => { void injectMercadoDevices(farmId); });
    });
    card.querySelector('.mpd-log')?.addEventListener('click', () => showMercadoLogModal(farmId, d.udid, d.label));
    card.querySelector('.mpd-history')?.addEventListener('click', () => openClickerHistoryWindow(farmId, d.udid, d.label));
    card.querySelector('.mpd-ops')?.addEventListener('click', () => openClickerOperationsWindow(farmId, d.udid, d.label));
    card.querySelector('.mpd-run')?.addEventListener('click', async () => {
        const btn = card.querySelector('.mpd-run');
        if (!btn)
            return;
        const action = d.polling ? 'stop' : 'start';
        if (action === 'stop' && !confirm(t('farmdev.mercado_confirm_stop', { name: d.label })))
            return;
        btn.disabled = true;
        try {
            const res = await fetch(`/api/farms/${farmId}/mercado-devices/${encodeURIComponent(d.udid)}/${action}`, { method: 'POST' });
            const data = await res.json().catch(() => ({}));
            if (res.ok)
                card.replaceWith(makeMercadoDeviceCard(data, farmId));
            else
                alert(data.error || t('farmdev.clicker_action_failed'));
        }
        catch {
            alert(t('farmdev.network_error'));
        }
        finally {
            btn.disabled = false;
        }
    });
    return card;
}
/** Header notice for what blocks the pollers farm-wide (runner missing / farm not set up). */
function renderNotice(body) {
    const header = document.querySelector('.farm-devs-header');
    if (!header)
        return;
    let el = header.querySelector('#mercado-notice');
    const text = !body.runnerConfigured ? t('farmdev.mercado_runner_off')
        : !body.runnerReachable ? t('farmdev.mercado_runner_down')
            : !body.farmReady ? t('farmdev.mercado_farm_not_ready')
                : '';
    if (!text) {
        el?.remove();
        return;
    }
    if (!el) {
        el = document.createElement('span');
        el.id = 'mercado-notice';
        el.className = 'settings-hint';
        el.style.color = 'var(--red)';
        header.appendChild(el);
    }
    el.textContent = text;
}
function mountAddButton(farmId) {
    if (!can('farm.settings'))
        return;
    const header = document.querySelector('.farm-devs-header');
    if (!header || header.querySelector('#mercado-add-device'))
        return;
    const btn = document.createElement('button');
    btn.className = 'tbl-btn';
    btn.id = 'mercado-add-device';
    btn.textContent = t('farmdev.mercado_add_btn');
    btn.addEventListener('click', () => showMercadoDeviceModal(farmId, null, () => { void injectMercadoDevices(farmId); }));
    header.appendChild(btn);
}
export async function injectMercadoDevices(farmId) {
    let body = null;
    try {
        const r = await fetch(`/api/farms/${farmId}/mercado-devices`);
        if (r.ok)
            body = await r.json();
    }
    catch { /* shown as unreachable below */ }
    const grid = document.getElementById('device-grid');
    if (!grid || !document.body.contains(grid))
        return;
    if (body)
        renderNotice(body);
    const devices = body?.devices ?? [];
    reconcileGrid(grid, devices.map((d) => ({ udid: d.udid, preserve: false, make: () => makeMercadoDeviceCard(d, farmId) })));
    if (!grid.querySelector('.device-card--mercado')) {
        const empty = document.createElement('p');
        empty.className = 'empty';
        empty.textContent = body ? t('farmdev.mercado_no_devices') : t('farmdev.mercado_list_failed');
        grid.appendChild(empty);
    }
}
/** Stop the active mercado device-grid poll; `keepFarmName` keeps it for a same-farm re-render. */
export function stopMercadoDevicePolling(keepFarmName) {
    if (!poll)
        return;
    if (keepFarmName !== undefined && poll.farmName === keepFarmName)
        return;
    window.clearInterval(poll.timer);
    poll = null;
}
/** Poll {@link injectMercadoDevices} while this farm's grid stays mounted. */
export function startMercadoDevicePolling(farmId, farmName) {
    const grid = document.getElementById('device-grid');
    if (!grid)
        return;
    if (poll && poll.farmName !== farmName)
        stopMercadoDevicePolling();
    else if (poll)
        window.clearInterval(poll.timer);
    mountAddButton(farmId);
    const timer = window.setInterval(() => {
        const g = document.getElementById('device-grid');
        if (!g || !document.body.contains(g)) {
            stopMercadoDevicePolling();
            return;
        }
        void injectMercadoDevices(farmId);
    }, POLL_MS);
    poll = { farmName, timer };
    void injectMercadoDevices(farmId);
}
